<template>
    <div>
        <div class="flex flex-row overflow-auto pl-0 justify-center lg:pt-2">
            <NuxtLink
                v-for="item in menuItems || []"
                :key="item._key"
                :to="getMenuItemUrl(item)"
                class="mr-8 last:mr-0 whitespace-nowrap border-b-2 border-transparent pb-1 font-monument-extended text-sm text-[#666666] duration-300 lg:text-xl"
                :class="{
                    '!border-green-600 text-black': isCurrentPage(item.internalLink._ref),
                }"
                @click="handleMenuClick($event, item)">
                {{ item.title }}
            </NuxtLink>
        </div>
        <div
            id="dynamic-buttons"
            class="mt-[38px] flex gap-4 overflow-auto pl-6 pr-0.5 lg:mt-10 lg:justify-center lg:px-0"></div>
    </div>
</template>

<script setup lang="ts">
    interface MenuItem {
        _key: string;
        title: string;
        internalLink: {
            _ref: string;
        };
        anchor?: string;
    }

    const props = defineProps<{
        menuItems: MenuItem[];
    }>();

    const route = useRoute();
    const ids = ref((props.menuItems || []).map((item: MenuItem) => item.internalLink._ref));

    const shopwareSlugs = await useGetShopwarePageById(ids);

    function isCurrentPage(itemRef: string): boolean {
        const itemSlug = shopwareSlugs[itemRef];
        return route.path === `/${itemSlug}`;
    }

    const HEADER_HEIGHT = 150;

    // Get the scroll container (same as scroll-behavior plugin)
    const getScrollContainer = (): HTMLElement | null => {
        const mainContent = document.querySelector('.main-content');
        if (mainContent instanceof HTMLElement) {
            return mainContent;
        }
        return null;
    };

    // Get menu item URL, extracting hash if present
    const getMenuItemUrl = (item: MenuItem): string => {
        const baseUrl = '/' + shopwareSlugs[item.internalLink._ref];
        // Check if item has an anchor/id property (from CMS)
        if (item.anchor) {
            return `${baseUrl}#${item.anchor}`;
        }
        // Check if the slug itself contains a hash
        const slug = shopwareSlugs[item.internalLink._ref];
        if (slug && slug.includes('#')) {
            return `/${slug}`;
        }
        return baseUrl;
    };

    // Scroll to element using the scroll container
    const scrollToElement = (hash: string) => {
        const element = document.querySelector(hash);
        if (!element) return;

        const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementTop - HEADER_HEIGHT;

        window.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: 'smooth',
        });
    };

    // Handle menu item click - check if it has a hash and scroll to it
    const handleMenuClick = (event: Event, item: MenuItem) => {
        const url = getMenuItemUrl(item);
        const hashMatch = url.match(/#(.+)$/);

        if (hashMatch) {
            const hash = hashMatch[1];
            const basePath = url.split('#')[0];
            const targetElement = document.querySelector(`#${hash}`);

            // If we're on the same page and the element exists, scroll to it
            if (targetElement && route.path === basePath) {
                event.preventDefault();
                nextTick(() => {
                    scrollToElement(`#${hash}`);
                });
            }
            // If navigating to a different page with hash, let NuxtLink handle navigation
            // The scroll-behavior plugin will handle scrolling after navigation completes
        }
    };

    const handleAnchorClick = (id: string) => {
        scrollToElement(`#${id}`);
    };

    const adjustScrollForHash = () => {
        const hash = window.location.hash.substring(1);
        if (hash) {
            handleAnchorClick(hash);
        }
    };

    const createDynamicButtons = () => {
        const dynamicButtonsContainer = document.getElementById('dynamic-buttons');
        if (!dynamicButtonsContainer) return;

        dynamicButtonsContainer.innerHTML = ''; // Clear previous buttons

        const faqDivs = document.querySelectorAll('div[_type="Faq"]');
        faqDivs.forEach(faqDiv => {
            faqDiv.querySelectorAll('h3').forEach(h3 => {
                const id = h3.id;
                const buttonText = h3.textContent;

                if (!id || !buttonText) return;

                const button = document.createElement('button');
                button.className =
                    'bg-green-300 px-8 py-4 rounded-full leading-none !w-max text-base hover:bg-green-600 hover:font-bold';
                button.innerText = buttonText;

                const anchor = document.createElement('a');
                anchor.href = `#${encodeURIComponent(id)}`;
                anchor.addEventListener('click', e => {
                    e.preventDefault();
                    handleAnchorClick(id);
                });
                anchor.appendChild(button);

                dynamicButtonsContainer.appendChild(anchor);
            });
        });
    };

    onMounted(() => {
        adjustScrollForHash();

        // Use MutationObserver to ensure the buttons are created when the relevant elements are available
        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (mutation.addedNodes.length) {
                    const faqDivs = document.querySelectorAll('div[_type="Faq"]');
                    if (faqDivs.length > 0) {
                        createDynamicButtons();
                        observer.disconnect(); // Disconnect the observer once the buttons are created
                    }
                }
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
    });

    watch(route, () => {
        nextTick(() => {
            createDynamicButtons();
            adjustScrollForHash();
        });
    });
</script>

<style></style>
